import {
  extractProductGroups,
  SectionBasedProduct,
  ProductGroupCarousel,
  FeaturedProductWidget,
} from "@brandclub/common-ui";
import { marked } from "marked";
import React from "react";
import {
  InlineCitation,
  InlineCitationCard,
  InlineCitationCardBody,
  InlineCitationCardTrigger,
  InlineCitationCarousel,
  InlineCitationCarouselContent,
  InlineCitationCarouselHeader,
  InlineCitationCarouselIndex,
  InlineCitationCarouselItem,
  InlineCitationCarouselNext,
  InlineCitationCarouselPrev,
  InlineCitationSource,
  InlineCitationText,
} from "@/components/ai-elements/inline-citation";
import { processProductGroupTags } from "./productGroupParser";

// Configure marked for proper HTML rendering
marked.use({
  gfm: true,
  breaks: true,
  pedantic: false,
});

/**
 * Extract citation tags from text and replace with placeholders
 */
export const extractCitationTags = (
  text: string
): {
  textWithPlaceholders: string;
  citations: Array<{
    placeholder: string;
    source: string;
    type?: string;
    title?: string;
    text: string;
  }>;
} => {
  const citations: Array<{
    placeholder: string;
    source: string;
    type?: string;
    title?: string;
    text: string;
  }> = [];

  let citationIndex = 0;
  let currentPos = 0;
  let result = "";

  while (currentPos < text.length) {
    // Find the next opening citation tag
    const citationStartIndex = text.indexOf("<citation", currentPos);

    if (citationStartIndex === -1) {
      // No more citations, add rest of text
      result += text.substring(currentPos);
      break;
    }

    // Add text before citation tag
    result += text.substring(currentPos, citationStartIndex);

    // Find the end of the opening tag
    const tagEndIndex = text.indexOf(">", citationStartIndex);
    if (tagEndIndex === -1) {
      // Malformed tag, skip it
      result += text.substring(citationStartIndex);
      break;
    }

    // Extract the opening tag content
    const openingTag = text.substring(citationStartIndex, tagEndIndex + 1);

    // Check if this is a self-closing tag (ends with /> or / >)
    // Handle cases like <citation ... /> or <citation ... / >
    const trimmedTag = openingTag.trim();
    const isSelfClosing =
      trimmedTag.endsWith("/>") || trimmedTag.match(/\/\s*>$/);

    // Parse attributes from the opening tag
    const sourceMatch = openingTag.match(/source\s*=\s*"([^"]+)"/);
    const typeMatch = openingTag.match(/type\s*=\s*"([^"]+)"/);
    const titleMatch = openingTag.match(/title\s*=\s*"([^"]+)"/);

    const source = sourceMatch ? sourceMatch[1] : "";
    const type = typeMatch ? typeMatch[1] : undefined;
    const title = titleMatch ? titleMatch[1] : undefined;

    if (isSelfClosing) {
      // Handle self-closing tag: <citation ... />
      const placeholder = `@@CITATION_${citationIndex}@@`;
      citations.push({
        placeholder,
        source,
        type,
        title,
        text: title || source, // Use title or source as the text for self-closing tags
      });

      result += placeholder;
      citationIndex++;
      currentPos = tagEndIndex + 1;
      continue;
    }

    // Find the closing tag for non-self-closing tags
    const closingTagIndex = text.indexOf("</citation>", tagEndIndex + 1);

    if (closingTagIndex === -1) {
      // No closing tag found, treat as self-closing with empty text
      const placeholder = `@@CITATION_${citationIndex}@@`;
      citations.push({
        placeholder,
        source,
        type,
        title,
        text: title || source, // Use title or source as the text
      });

      result += placeholder;
      citationIndex++;
      currentPos = tagEndIndex + 1;
      continue;
    }

    // Extract the text content between tags
    const citationText = text.substring(tagEndIndex + 1, closingTagIndex);

    // Create placeholder
    const placeholder = `@@CITATION_${citationIndex}@@`;
    citations.push({
      placeholder,
      source,
      type,
      title,
      text: citationText,
    });

    result += placeholder;
    citationIndex++;

    // Move past the closing tag
    currentPos = closingTagIndex + 11; // "</citation>".length
  }

  return {
    textWithPlaceholders: result,
    citations,
  };
};

/**
 * Render message content with product group carousels and featured products embedded
 */
export const renderMessageContentWithCarousels = (
  text: string,
  productMetadataMap: Map<string, SectionBasedProduct>,
  streamingGroups: Map<number, string[]>,
  isStreaming: boolean = false,
  isAlreadyProcessed: boolean = false
): React.ReactNode[] => {
  // Extract citation tags and replace with placeholders BEFORE processing
  const { textWithPlaceholders: textWithCitations, citations } =
    extractCitationTags(text);

  // Extract featured product tags and replace with placeholders BEFORE processing
  // This preserves their positions in the message
  // Handle both self-closing tags (/>) and tags with optional closing slash
  // Also handle flexible whitespace (including newlines) around attributes
  const featuredProductRegex =
    /<product\s+featured\s*=\s*"true"\s+stacklineSku\s*=\s*"([^"]+)"(?:\s+question\s*=\s*"([^"]+)")?\s*\/?>/g;
  const featuredProducts: Array<{
    placeholder: string;
    stacklineSku: string;
    question: string;
  }> = [];

  let featuredProductIndex = 0;
  let textWithFeaturedProductPlaceholders = textWithCitations.replace(
    featuredProductRegex,
    (match, stacklineSku, question) => {
      // Use a placeholder format that markdown won't interpret
      // Using @@ which markdown doesn't interpret
      const placeholder = `@@FEATURED_PRODUCT_${featuredProductIndex}@@`;
      featuredProducts.push({
        placeholder,
        stacklineSku,
        question: question || "",
      });
      featuredProductIndex++;
      return placeholder;
    }
  );

  // Process product group tags BEFORE markdown parsing (if not already processed)
  // For streaming messages, text is already processed with |||PRODUCT_GROUP_N||| placeholders
  // For loaded messages, we need to process the raw <product_group> tags first
  const processedText = isAlreadyProcessed
    ? textWithFeaturedProductPlaceholders
    : processProductGroupTags(textWithFeaturedProductPlaceholders);

  // Now parse markdown
  const htmlContent = marked.parse(processedText) as string;

  // Remove <hr> elements from the parsed HTML
  const htmlContentWithoutHr = htmlContent.replace(/<hr\s*\/?>/gi, "");

  // Wrap tables in scrollable containers for mobile responsiveness
  const htmlContentWithWrappedTables = htmlContentWithoutHr.replace(
    /<table[\s\S]*?<\/table>/gi,
    (match) => `<div class="table-scroll-wrapper">${match}</div>`
  );

  // Split by product group placeholders, featured product placeholders, AND citation placeholders
  // NOTE: pipes must be escaped in the regex, otherwise the split matches empty strings
  // The regex uses capturing groups so the placeholders are included in the split results
  const parts = htmlContentWithWrappedTables.split(
    /(\|\|\|PRODUCT_GROUP_\d+\|\|\||@@FEATURED_PRODUCT_\d+@@|@@CITATION_\d+@@)/
  );

  // Extract product groups from the original text to get stacklineSku references
  const productGroups = extractProductGroups(text);

  return parts.map((part, idx) => {
    // Check if this part is a product group placeholder
    const groupMatch = part.match(/\|\|\|PRODUCT_GROUP_(\d+)\|\|\|/);
    if (groupMatch) {
      const groupIndex = parseInt(groupMatch[1]);

      // For streaming: use streamingGroups if available, otherwise extract from text
      // For loaded: always extract from text
      let allStacklineSkus: string[];
      if (isStreaming && streamingGroups.has(groupIndex)) {
        allStacklineSkus = streamingGroups.get(groupIndex) || [];
      } else {
        const group = productGroups[groupIndex];
        allStacklineSkus = group?.products.map((p) => p.stacklineSku) || [];
      }

      // Map stacklineSkus to actual product metadata from the centralized map
      const groupProductsWithMetadata = allStacklineSkus
        .map((sku) => productMetadataMap.get(sku))
        .filter(Boolean) as SectionBasedProduct[];

      return (
        <ProductGroupCarousel
          key={`group-${idx}`}
          products={groupProductsWithMetadata}
          allStacklineSkus={allStacklineSkus}
          isLoading={isStreaming && allStacklineSkus.length === 0}
        />
      );
    }

    // Check if this part is a featured product placeholder
    const featuredMatch = part.match(/@@FEATURED_PRODUCT_(\d+)@@/);
    if (featuredMatch) {
      const featuredIndex = parseInt(featuredMatch[1]);
      const featuredProduct = featuredProducts[featuredIndex];

      if (featuredProduct) {
        const product = productMetadataMap.get(featuredProduct.stacklineSku);
        if (product) {
          return (
            <FeaturedProductWidget
              key={`featured-${idx}-${featuredProduct.stacklineSku}`}
              product={product}
            />
          );
        } else {
          // Product metadata not available - this usually means the API didn't return product data for this SKU
          // Log a warning for debugging but don't render a widget
          if (process.env.NODE_ENV === "development") {
            console.warn(
              `FeaturedProductWidget: Product metadata not found for SKU: ${featuredProduct.stacklineSku}. ` +
                `Available SKUs: ${Array.from(productMetadataMap.keys()).join(
                  ", "
                )}`
            );
          }
        }
      }
      // If product not found, return empty fragment
      return <React.Fragment key={`featured-missing-${idx}`} />;
    }

    // Check if this part is a citation placeholder
    // The placeholder might be standalone or wrapped in HTML tags from markdown parsing
    const citationMatch = part.match(/@@CITATION_(\d+)@@/);
    if (citationMatch) {
      const citationIndex = parseInt(citationMatch[1]);
      const citation = citations[citationIndex];

      if (citation && citation.source) {
        // Use title or source as fallback if text is empty or whitespace
        const displayText =
          citation.text?.trim() || citation.title || citation.source;

        // Create a source object for the InlineCitation component
        const source = {
          title: citation.title || citation.source,
          url: citation.source,
          description: citation.text?.trim() || undefined,
        };

        return (
          <InlineCitation key={`citation-${idx}`}>
            <InlineCitationText url={citation.source}>
              {displayText}
            </InlineCitationText>
            <InlineCitationCard>
              <InlineCitationCardTrigger sources={[citation.source]} />
              <InlineCitationCardBody>
                <InlineCitationCarousel>
                  <InlineCitationCarouselHeader>
                    <InlineCitationCarouselPrev />
                    <InlineCitationCarouselNext />
                    <InlineCitationCarouselIndex />
                  </InlineCitationCarouselHeader>
                  <InlineCitationCarouselContent>
                    <InlineCitationCarouselItem>
                      <InlineCitationSource
                        description={source.description}
                        title={source.title}
                        url={source.url}
                      />
                    </InlineCitationCarouselItem>
                  </InlineCitationCarouselContent>
                </InlineCitationCarousel>
              </InlineCitationCardBody>
            </InlineCitationCard>
          </InlineCitation>
        );
      }
      // If citation not found or invalid, return empty fragment
      return <React.Fragment key={`citation-missing-${idx}`} />;
    }

    // Regular content - use dangerouslySetInnerHTML to render HTML
    // Skip empty parts (they might be empty strings from splitting)
    if (!part.trim()) {
      return <React.Fragment key={`empty-${idx}`} />;
    }

    return (
      <div
        key={idx}
        style={{ display: "flex", flexDirection: "column" }}
        dangerouslySetInnerHTML={{ __html: part }}
        className="message-markdown"
      />
    );
  });
};
